<template>
  <!-- The contract of a self-service subscription: term and payment option,
       with what `seats` cost on the chosen option; the tier table of both
       options is collapsed until asked for. Nothing is preselected unless
       the parent sets the models: both are required. -->
  <div class="flex flex-col gap-4">
    <CardSelect
      v-model="term"
      :label="t('licensing.contract.termLabel')"
      :options="termOptions"
    />

    <CardSelect
      v-if="!optionFixed"
      v-model="option"
      :label="t('licensing.contract.optionLabel')"
      :options="paymentOptions"
    />
    <!-- a trial billed by Stripe keeps the option chosen at the card step -->
    <p v-else class="text-sm text-content dark:text-content-dark">
      {{
        t("licensing.contract.optionFixed", {
          option: option ? t(billingIntervalKey(option)) : "",
        })
      }}
    </p>

    <p class="text-xs text-muted-text dark:text-muted-text-dark">
      {{ t("licensing.contract.renewsNote") }}
    </p>

    <!-- how the chosen option's total adds up -->
    <p
      v-if="option && breakdownParts"
      class="text-sm text-heading dark:text-heading-dark"
    >
      {{
        t(
          taxIncluded
            ? "licensing.price.breakdownTaxIncluded"
            : "licensing.price.breakdown",
          {
            parts: breakdownParts,
            total: periodTotal(option, seats),
            interval: intervalLabel(option),
          },
        )
      }}
    </p>

    <Accordion v-if="price" :title="t('licensing.price.tiersTitle')">
      <SeatPriceTable :org-id="orgId" :seats="seats" :selected="option" />
    </Accordion>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { Org } from "tf-api-client";
import {
  billingIntervalKey,
  CONTRACT_TERMS,
  PAYMENT_OPTIONS,
} from "../../config/licensing";
import { useSeatPrice } from "../../composables/use-seat-price";
import i18nUtil from "../../utils/i18n";
import Accordion from "../Base/Accordion.vue";
import CardSelect from "../Base/CardSelect.vue";
import SeatPriceTable from "./SeatPriceTable.vue";

const props = defineProps<{
  orgId: string | null;
  // the seats the price is shown for
  seats: number;
  // the payment option can't change (shown read-only)
  optionFixed?: boolean;
}>();

const term = defineModel<Org.ContractTermMonths | null>("term", {
  default: null,
});
const option = defineModel<Org.SubscriptionBillingInterval | null>("option", {
  default: null,
});

const { t } = useI18n();
const { price, intervalLabel, format, breakdown, periodTotal, taxIncluded } =
  useSeatPrice(() => props.orgId);

const termOptions = computed(() =>
  CONTRACT_TERMS.map((months) => ({
    value: months,
    label: t("licensing.contract.termValue", { months }),
  })),
);

const paymentOptions = computed(() =>
  PAYMENT_OPTIONS.map((value) => ({
    value,
    label: t(`licensing.contract.option.${value}`),
    description: t(`licensing.contract.optionHint.${value}`),
  })),
);

// "49 × CHF 54.00 + 11 × CHF 36.00"
const breakdownParts = computed(() => {
  if (!option.value) return "";
  return breakdown(option.value, props.seats)
    .map(
      (row) =>
        `${i18nUtil.formatNumber(row.seats)} × ${format(row.tier.unitAmount)}`,
    )
    .join(" + ");
});
</script>
